import type { EditorContextProps } from '@tunasong/editor'
import { ElementEditor, useCreateEditor } from '@tunasong/editor'
import type { TunaEditor } from '@tunasong/plugin-lib'
import { usePlugins } from '@tunasong/plugin-lib'
import { isCoreElement } from '@tunasong/schemas'
import { useEffect } from 'react'

export interface ChatEditorProps extends EditorContextProps {
  onEditor?(editor: TunaEditor): void
}

const ChatElementEditor = (props: ChatEditorProps) => {
  const { element, onEditor, ...restProps } = props
  const chatPlugins = usePlugins('chat')

  const editor = useCreateEditor(element, chatPlugins)
  useEffect(() => {
    if (!editor) {
      return
    }
    onEditor?.(editor)
  }, [editor, onEditor])

  return chatPlugins && editor && isCoreElement(element) ? (
    <ElementEditor {...restProps} editor={editor} element={element} />
  ) : null
}

/**
 * `useCreateEditor` makes the editor once, from the first element it is given. A read-only comment can first render
 * from a copy without its body (an optimistic or fields-only copy), so its editor stayed empty when the full comment
 * arrived: a blank comment in the sidebar Chat (GH #3146). Read-only, it is made again once the body is there. The
 * editable composer keeps its editor: what the user types lives there.
 */
export const ChatEditor = (props: ChatEditorProps) => {
  const { element, readOnly } = props
  const hasBody = isCoreElement(element) && Boolean(element.children?.length)

  return <ChatElementEditor key={readOnly && hasBody ? 'body' : 'no-body'} {...props} />
}
